import { isNil } from 'lodash';
import { useContext, useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { Styles } from '@shared/components/button';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2Bold } from '@shared/components/typography';
import {
  SingleComponentForm,
  SingleComponentFormProps,
} from '@shared/scenes/cards/singleComponentForms/SingleComponentForm';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { CollectionViewAnalytics } from '../../../CollectionViewAnalytics';
import { CardCollectionViewFieldContainer } from '../../fields/CardCollectionViewFieldContainer';
import { WorkflowEntityProvider, useField, useLoadingAndError, useUpdateField } from './provider';

interface InputButtonProps {
  component: API.InputButtonComponent;
  style: StyleProp<ViewStyle>;
  workflowTemplateId: string;
  entityId: string;
  analytics?: CollectionViewAnalytics;
  canUpdateCard?: boolean;
  paddingTop?: number;
  isFullWidth?: boolean;
}

type InputButtonFormProps = Omit<SingleComponentFormProps, 'field' | 'loading' | 'onUpdateField'>;

export function InputButton(props: InputButtonProps) {
  if (!props.canUpdateCard) {
    return null;
  }

  return <InputButtonInner {...props} />;
}

function InputButtonForm({
  fieldName,
  anchorName,
  workflowTemplate,
  onClose,
}: InputButtonFormProps) {
  const { loading } = useLoadingAndError();
  const field = useField(fieldName);
  const onUpdateField = useUpdateField();

  return (
    <SingleComponentForm
      fieldName={fieldName}
      field={field}
      anchorName={anchorName}
      workflowTemplate={workflowTemplate}
      onClose={onClose}
      loading={loading}
      onUpdateField={onUpdateField}
    />
  );
}

function InputButtonInner({
  component,
  style,
  workflowTemplateId,
  entityId,
  analytics,
  paddingTop = 0,
  isFullWidth,
}: InputButtonProps) {
  const anchorName = `--input-button-${entityId}-${component.inputComponentId}`;
  const [showForm, setShowForm] = useState(false);
  const colors = useContext(ColorThemeContext);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const onPress = () => {
    analytics?.trackButtonClick(entityId);
    setShowForm(prev => !prev);
  };

  return (
    <CardCollectionViewFieldContainer style={[style, { paddingTop }]}>
      <View style={{ width: '100%', anchorName }}>
        <MBTouchableOpacity onPress={onPress}>
          <MBView
            center
            paddingHorizontal={2.5}
            style={[
              Styles.button,
              Styles.buttonSizeSmall,
              {
                backgroundColor: colors.button.backgroundInverted,
                borderColor: showForm ? Colors.v2.gray30 : colors.button.foregroundInverted,
                alignSelf: 'flex-start',
                width: isFullWidth ? '100%' : 'auto',
              },
            ]}
          >
            <MBTextBody2Bold
              numberOfLines={1}
              ellipsizeMode={'tail'}
              color={showForm ? Colors.v2.gray30 : colors.button.foregroundInverted}
            >
              {component.buttonText}
            </MBTextBody2Bold>
          </MBView>
        </MBTouchableOpacity>
        {showForm && !isNil(workflowTemplate) && (
          <WorkflowEntityProvider entityId={entityId}>
            <InputButtonForm
              fieldName={component.inputComponentId}
              anchorName={anchorName}
              onClose={() => setShowForm(false)}
              workflowTemplate={workflowTemplate}
            />
          </WorkflowEntityProvider>
        )}
      </View>
    </CardCollectionViewFieldContainer>
  );
}
